// Autometrics dashboard v3 - data preparation and derived columns
// (spec sections 2, 3 and 4). Pure functions with no React or browser
// dependencies, so this module can move unchanged into the Node data layer.

import {
  VEHICLE_CAPACITY, CO2_GROUPS, EMISSION_FACTORS, DISTANCE_PARAMS, POD_FIELDS, FTL_WEIGHT_SOURCE, CO2_BASELINE,
} from './amConfig';
import { PINCODE_AREAS, SOURCE_LOCATIONS, LANE_KM } from './amPincodeAreas';

const DAY_MS = 86400000;
const CO2_GROUP_BY_UPPER = Object.fromEntries(Object.entries(CO2_GROUPS).map(([e, g]) => [e.toUpperCase(), g]));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FTL_STATUS = {
  0: 'At Source', 1: 'Dispatched from Source', 2: 'In Transit', 3: 'Arrived At Destination',
  4: 'Out For Delivery', 5: 'Delivered', 6: 'Force Closure', 7: 'Provision', 8: 'Delivered',
  9: 'Cancelled', 10: 'Trip Created', 11: 'Undelivered',
};

export const EXCUSED_CATEGORIES = ['Customer Issue', 'Local/Public Holidays', 'Natural Calamity'];
export const POD_BUCKETS = ['On-time', '1-2 days', '3-5 days', '6-10 days', 'Above 10 days', 'Blank'];

const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
const str = (v) => (isBlank(v) ? '' : String(Array.isArray(v) ? v.join(' ') : v).trim());
const num = (v) => {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Local calendar day index (days since epoch in the browser's zone). Times are ignored. */
export function dayOf(v) {
  if (isBlank(v)) return null;
  const d = v instanceof Date ? v : new Date(v);
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return Math.floor((t - d.getTimezoneOffset() * 60000) / DAY_MS);
}

export function dayToDate(day) {
  const d = new Date(day * DAY_MS);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function dayLabel(day) {
  if (day === null || day === undefined) return '';
  const d = new Date(day * DAY_MS);
  return `${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
}

export function monthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

function properCase(s) {
  return s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

export function remarkCategory(raw) {
  const s = str(raw).toLowerCase().replace(/\s+/g, '');
  if (!s) return 'None';
  if (s.startsWith('customerissue')) return 'Customer Issue';
  if (s.startsWith('transporterissue')) return 'Transporter Issue';
  if (s.includes('holiday')) return 'Local/Public Holidays';
  if (s.startsWith('naturalcalamity')) return 'Natural Calamity';
  if (s.startsWith('others')) return 'Others';
  return 'Other carrier remark';
}

const VEHICLE_ALIASES = {
  COURRIER: 'COURIER', TATAACE: 'TATA ACE', '17 FT': '17 F', '32FT CONTAINER': '32 FT CONTAINER',
};
const VEHICLE_BLANKS = new Set(['', 'N/A', '0', 'S']);

export function cleanVehicleType(raw) {
  const s = str(raw).toUpperCase().replace(/\s+/g, ' ');
  if (VEHICLE_BLANKS.has(s)) return 'Not specified';
  return VEHICLE_ALIASES[s] || s;
}

/** Performance as exported (col AW): Ontime / Delay / blank. */
function exportPerformance(r) {
  if (str(r.sla_considered).toLowerCase() === 'yes') return 'Ontime';
  const os = num(r.ontime_status);
  const td = num(r.transit_delay);
  if (os === 1 && td === 0) return 'Ontime';
  if (os === 0 || td === 1) return 'Delay';
  return '';
}

/** New Performance rule (spec section 4). */
export function newPerformance(perf, remarkCat, delayDays) {
  if (perf === 'Ontime') return 'On Time';
  if (perf === 'Delay' && EXCUSED_CATEGORIES.includes(remarkCat)) return 'On Time';
  if (perf === '' && delayDays === 0) return 'On Time';
  return 'Delay';
}

function statusLabel(r) {
  const cs = str(r.consignment_status);
  if (cs) return cs;
  const s = r.status;
  if (typeof s === 'number') return FTL_STATUS[s] || 'Not available';
  return str(s) || 'Not available';
}

function isDelivered(r, label) {
  if (label.toLowerCase() === 'delivered') return true;
  return str(r.tracking_type).toUpperCase() === 'FTL' && [5, 8].includes(num(r.status));
}

function emissionMode(mode, fuel) {
  const m = mode.toLowerCase();
  const f = fuel.toLowerCase();
  if (m.includes('air') || f.includes('air')) return 'Air';
  if (m.includes('rail') || f.includes('rail')) return 'Rail';
  if (/\bev\b|electric/.test(f)) return 'EV';
  if (f.includes('cng')) return 'CNG';
  return 'Road';
}

function haversineKm(a, b) {
  const rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad;
  const dLng = (b[1] - a[1]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

const coordsOf = (pin) => (pin ? (SOURCE_LOCATIONS[pin] || PINCODE_AREAS[pin.slice(0, 3)] || null) : null);

/** Straight-line km between two pincode areas, or null when either is unknown. */
export function pincodeGreatCircleKm(srcPin, dstPin) {
  const a = coordsOf(srcPin);
  const b = coordsOf(dstPin);
  return a && b ? haversineKm(a, b) : null;
}

/** Reference CO2 settings (spec section 5); the CO2 page can override them. */
export const CO2_DEFAULTS = {
  ef: { ...EMISSION_FACTORS },
  roadFactor: DISTANCE_PARAMS.roadFactor,
  minKm: DISTANCE_PARAMS.minKm,
  base: Object.fromEntries(Object.entries(CO2_BASELINE).map(([g, b]) => [g, { ...b }])),
};

/**
 * Sets distKm / distSource / co2 on every row from the CO2 settings, in
 * priority order: sign-off straight-line km x road factor (min km; air uses
 * straight-line); else the TMS road km as-is; else the pincode estimate.
 * Mutates rows in place.
 */
export function applyCo2Settings(rows, cfg) {
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    let km = null; let src = null;
    const straight = (gc) => Math.max(cfg.minKm, r.efMode === 'Air' ? gc : gc * cfg.roadFactor);
    if (r.gcKm !== null && r.gcKm !== undefined) {
      km = straight(r.gcKm); src = r.gcSource;
    } else if (r.roadKm !== null && r.roadKm !== undefined) {
      km = r.roadKm; src = r.roadSource;
    } else if (r.pinGcKm !== null && r.pinGcKm !== undefined) {
      km = straight(r.pinGcKm); src = 'Pincode estimate';
    }
    r.distKm = km;
    r.distSource = src;
    const ef = cfg.ef[r.efMode];
    r.co2 = r.co2Group && km !== null && r.weight !== null && ef !== undefined ? (km * (r.weight / 1000) * ef) / 1e6 : null;
  }
  return rows;
}

export function distanceKm(srcPin, dstPin, efMode, params = DISTANCE_PARAMS) {
  const override = LANE_KM[`${srcPin}-${dstPin}`];
  if (override !== undefined) return override;
  const a = coordsOf(srcPin);
  const b = coordsOf(dstPin);
  if (!a || !b) return null;
  const gc = haversineKm(a, b);
  if (efMode === 'Air') return gc;
  return Math.max(params.minKm, gc * params.roadFactor);
}

function podReceived(r) {
  const status = str(r[POD_FIELDS.status]).toLowerCase();
  const date = dayOf(r[POD_FIELDS.receivedDate]);
  if (POD_FIELDS.systemOfRecord === 'date') return date !== null;
  if (r[POD_FIELDS.status] === true || r[POD_FIELDS.status] === 1) return true;
  return POD_FIELDS.receivedValues.includes(status);
}

export function podBucket(age) {
  if (age === null) return 'Blank';
  if (age <= 0) return 'On-time';
  if (age <= 2) return '1-2 days';
  if (age <= 5) return '3-5 days';
  if (age <= 10) return '6-10 days';
  return 'Above 10 days';
}

const compareDay = (delivery, ref) => {
  if (delivery === null || ref === null) return null;
  if (delivery < ref) return 'Early';
  if (delivery === ref) return 'On date';
  return 'Late';
};

/**
 * Raw API rows -> cleaned fact rows + data-quality counts.
 * refreshDay: day index used for pending POD ageing (defaults to today).
 * fromDay / toDay: selected dispatch window; rows dispatched outside it are dropped.
 */
export function prepareRows(raw, { refreshDay = dayOf(new Date()), fromDay = null, toDay = null } = {}) {
  const dq = {
    fetched: raw.length, notInExport: 0, outsideRange: 0, raw: 0, documentPurpose: 0, duplicates: 0, analysed: 0,
    deliveryBeforeDispatch: 0, deliveredNoDate: 0, missingDestStateZone: 0,
    primary: 0, primaryOwnPin: 0, cbmOver100: 0, noDistance: 0, vehicleTypesNoCapacity: [],
    missingEta: 0, missingCpd: 0, missingEdd: 0, blankVehicleType: 0, zeroWeight: 0,
    blankPerformance: 0, blankPerformanceDelayZero: 0,
    ftlWeightTripKg: 0, ftlWeightInvoiceKg: 0, ftlNoInvoiceLines: 0, ftlTinyWeight: 0,
    podBlank: 0, podDateBeforeDelivery: 0, podReceivedNoDate: 0,
  };

  // Row filters, in spec order.
  const seen = new Set();
  const kept = [];
  raw.forEach((r) => {
    if (!r) return;
    // Step 0: same eligibility as the consignments "All" tab the sign-off
    // extract was exported from (cancelled / force-closed trips never appear).
    const tt = str(r.tracking_type).toUpperCase();
    const inExport = (tt === 'FTL' && ![6, 9].includes(num(r.status)))
      || (tt === 'PTL' && str(r.consignment_status) !== 'Cancelled');
    if (!inExport || r.lr_no === undefined || r.lr_no === '') { dq.notInExport += 1; return; }
    // The API's date window is broader than dispatch date (UTC day range,
    // invoice-date fallback), so enforce the dispatch window here.
    const dd = dayOf(r.dispatch_date) ?? dayOf(r.invoice_time);
    if ((fromDay !== null && (dd === null || dd < fromDay)) || (toDay !== null && (dd === null || dd > toDay))) {
      dq.outsideRange += 1; return;
    }
    const lr = str(r.lr_no);
    if (lr.toLowerCase() === 'document purpose') { dq.documentPurpose += 1; return; }
    const key = `${lr}|${str(r.invoice_no)}|${str(r.transporter_name)}`;
    if (seen.has(key)) { dq.duplicates += 1; return; }
    seen.add(key);
    kept.push(r);
  });

  const sourcePins = new Set(kept.map((r) => str(r.consigner_pincode)).filter(Boolean));
  const noCapTypes = new Set();

  const rows = kept.map((r, i) => {
    const type = str(r.tracking_type).toUpperCase() || 'Not specified';
    const status = statusLabel(r);
    const delivered = isDelivered(r, status);
    const dispatch = dayOf(r.dispatch_date) ?? dayOf(r.invoice_time);
    const delivery = dayOf(r.delivery_datetime);
    const eta = dayOf(type === 'FTL' ? (r.expected_trip_end || r.expected_eta) : (r.expected_eta || r.expected_trip_end));
    const cpd = dayOf(r.final_eta_dt);
    // EDD exactly as the consignments export's "Estimated Delivery Date" column:
    // estimated_delivery_date, else initial_delivery_date. (Revised ETA is reset
    // to the actual delivery time on delivery, so it is not a reference date.)
    const edd = dayOf(r.estimated_delivery_date || r.initial_delivery_date);

    let monthKey = null; let year = null; let quarter = null;
    if (dispatch !== null) {
      const d = new Date(dispatch * DAY_MS);
      year = String(d.getUTCFullYear());
      monthKey = `${year}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      quarter = `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${year}`;
    }

    const perf = exportPerformance(r);
    const remarkCat = remarkCategory(r.late_delivery_remarks || r.reason_for_delay);
    // Delay Days exactly as the consignments export derives it (col AX):
    // ceil(transit_delay_hours / 24), and 0 when hours are missing.
    const delayHours = num(r.transit_delay_hours);
    const delayDays = delayHours !== null ? Math.ceil(delayHours / 24) : (num(r.transit_delay_days) ?? 0);
    const newPerf = newPerformance(perf, remarkCat, delayDays);
    const excused = perf === 'Delay' && EXCUSED_CATEGORIES.includes(remarkCat);

    const srcPin = str(r.consigner_pincode);
    const dstPin = str(r.consignee_pincode);
    const srcCity = properCase(str(r.consigner_city)) || 'Not specified';
    const dstCity = properCase(str(r.consignee_city)) || 'Not specified';
    const dstStateRaw = str(r.consignee_state);
    const dstZoneRaw = str(r.consignee_zone);
    const primary = dstPin !== '' && sourcePins.has(dstPin);

    const vehicle = cleanVehicleType(r.vehicle_type || r.truck_type);
    const cap = VEHICLE_CAPACITY[vehicle] || null;
    // FTL: the trip record's total_weight / total_cbm are not the full load;
    // use the sum of the trip's invoice lines when available (FTL_WEIGHT_SOURCE).
    const isFtl = type === 'FTL';
    const legWeight = num(r.leg_weight);
    const useLegs = isFtl && FTL_WEIGHT_SOURCE === 'invoices' && legWeight !== null && legWeight > 0;
    const weightRaw = useLegs ? legWeight : num(r.total_weight);
    const weight = weightRaw !== null && weightRaw > 0 ? weightRaw : null;
    const cbmRaw = useLegs && num(r.leg_cbm) !== null && num(r.leg_cbm) > 0 ? num(r.leg_cbm) : num(r.total_cbm);
    const cbm = cbmRaw !== null && cbmRaw > 0 && cbmRaw <= 100 ? cbmRaw : null;

    const validDelivery = delivery !== null && dispatch !== null && delivery >= dispatch;
    const deliveryDays = validDelivery ? delivery - dispatch : null;
    const breachDays = delivery !== null && eta !== null ? Math.max(0, delivery - eta) : null;

    const entity = str(r.truck_seal_entity || r.entity) || 'Not specified';
    // Entity names arrive in capitals ("LEGRAND"), so match case-insensitively.
    const co2Group = CO2_GROUP_BY_UPPER[entity.toUpperCase()] || null;
    const mode = str(r.shipment_mode);
    const fuel = str(r.fuel_type);
    const efMode = emissionMode(mode, fuel);
    // Distance inputs only - km and CO2 are set by applyCo2Settings() below so
    // the CO2 calculation settings can change them without reloading.
    const gcKm = num(r.gc_km);
    const laneKm = num(r.lane_km);
    const pinGc = gcKm === null ? pincodeGreatCircleKm(srcPin, dstPin) : null;
    const pinLaneKm = LANE_KM[`${srcPin}-${dstPin}`];

    let pod = null; let podDate = null; let podAge = null; let podBkt = null;
    if (delivered) {
      const received = podReceived(r);
      pod = received ? 'Received' : 'Pending';
      podDate = dayOf(r[POD_FIELDS.receivedDate]);
      if (delivery !== null) {
        const end = received ? podDate : refreshDay;
        podAge = end !== null ? end - delivery : null;
      }
      podBkt = podBucket(podAge);
      if (isBlank(r[POD_FIELDS.status])) dq.podBlank += 1;
      if (podDate !== null && delivery !== null && podDate < delivery) dq.podDateBeforeDelivery += 1;
      if (received && podDate === null) dq.podReceivedNoDate += 1;
    }

    // Data-quality tallies.
    if (delivery !== null && dispatch !== null && delivery < dispatch) dq.deliveryBeforeDispatch += 1;
    if (delivered && delivery === null) dq.deliveredNoDate += 1;
    if (!dstStateRaw || !dstZoneRaw) dq.missingDestStateZone += 1;
    if (primary) dq.primary += 1;
    if (primary && dstPin === srcPin) dq.primaryOwnPin += 1;
    if (cbmRaw !== null && cbmRaw > 100) dq.cbmOver100 += 1;
    if (gcKm === null && pinGc === null && !(laneKm > 0) && pinLaneKm === undefined) dq.noDistance += 1;
    if (eta === null) dq.missingEta += 1;
    if (cpd === null) dq.missingCpd += 1;
    if (edd === null) dq.missingEdd += 1;
    if (vehicle === 'Not specified') dq.blankVehicleType += 1;
    if (weight === null) dq.zeroWeight += 1;
    if (isFtl) {
      dq.ftlWeightTripKg += Math.max(0, num(r.total_weight) || 0);
      dq.ftlWeightInvoiceKg += Math.max(0, legWeight || 0);
      if (legWeight === null || legWeight <= 0) dq.ftlNoInvoiceLines += 1;
      if (weight !== null && weight < 50) dq.ftlTinyWeight += 1;
    }
    if (delivered && perf === '') {
      dq.blankPerformance += 1;
      if (delayDays === 0) dq.blankPerformanceDelayZero += 1;
    }
    if (type === 'FTL' && !cap && vehicle !== 'Not specified') noCapTypes.add(vehicle);

    const veh = str(r.truck_no).toUpperCase();
    const tptCat = str(r.tpt_category) || 'Not specified';

    return {
      id: i,
      lr: str(r.lr_no), inv: str(r.invoice_no), veh,
      type, status, delivered,
      dispatch, delivery, eta, cpd, edd,
      year, quarter, month: monthKey,
      perf, remarkCat, delayDays, newPerf, excused,
      onTimeResult: delivered ? newPerf : 'Not delivered',
      exception: excused ? remarkCat : 'No exception',
      entity,
      transporter: str(r.transporter_name).toUpperCase() || 'Not specified',
      tptCat,
      org: tptCat.toUpperCase() === 'API' ? 'Organised' : 'Unorganised / manual',
      srcCity, srcPin, srcZone: properCase(str(r.consigner_zone)) || 'Not specified',
      dstCity, dstPin,
      dstState: dstStateRaw ? properCase(dstStateRaw) : 'Not specified',
      dstZone: dstZoneRaw ? properCase(dstZoneRaw) : 'Not specified',
      lane: `${srcCity} → ${dstCity}`,
      primSec: primary ? 'Primary' : 'Secondary',
      movement: str(r.movement_type) || 'Not specified',
      invoiceType: properCase(str(r.invoice_type)) || 'Not specified',
      vehicle, capKg: cap ? cap.kg : null, capCbm: cap ? cap.cbm : null,
      weight, weightSource: isFtl ? (useLegs ? 'Invoice lines' : 'Trip record') : 'LR record', cbm, mode: mode || 'Not specified', fuel: fuel || 'Not specified',
      validDelivery, deliveryDays, breachDays,
      etaRes: validDelivery ? compareDay(delivery, eta) : null,
      cpdRes: validDelivery ? compareDay(delivery, cpd) : null,
      eddRes: validDelivery ? compareDay(delivery, edd) : null,
      pod, podDate, podAge, podBucket: podBkt,
      tripId: type === 'FTL' && dispatch !== null
        ? `${veh || `NO-VEHICLE-${str(r.lr_no)}`}-${dayLabel(dispatch)}-${str(r.transporter_name).toUpperCase()}`
        : null,
      co2Group, efMode,
      // distance inputs: sign-off straight-line km, TMS road km, pincode straight-line km
      gcKm, gcSource: gcKm !== null ? (str(r.gc_km_source) || 'Sign-off lane') : null,
      pinGcKm: pinGc,
      roadKm: laneKm !== null && laneKm > 0 ? laneKm : (pinLaneKm !== undefined ? pinLaneKm : null),
      roadSource: laneKm !== null && laneKm > 0 ? (str(r.lane_km_source) || 'TMS lane') : (pinLaneKm !== undefined ? 'Lane master' : null),
      distKm: null, distSource: null, co2: null,
    };
  });

  applyCo2Settings(rows, CO2_DEFAULTS);
  dq.analysed = rows.length;
  dq.raw = dq.fetched - dq.notInExport - dq.outsideRange;
  dq.vehicleTypesNoCapacity = [...noCapTypes].sort();
  return { rows, dq };
}

/**
 * FTL trip table (trip grain). A trip takes lane, transporter, vehicle type,
 * entity and month of its heaviest row.
 */
export function buildTrips(rows) {
  const map = new Map();
  rows.forEach((r) => {
    if (!r.tripId) return;
    let t = map.get(r.tripId);
    if (!t) {
      t = { id: r.tripId, rows: [], weight: 0, cbm: 0, cbmComplete: true, heaviest: null };
      map.set(r.tripId, t);
    }
    t.rows.push(r);
    t.weight += r.weight || 0;
    if (r.cbm === null) t.cbmComplete = false; else t.cbm += r.cbm;
    if (!t.heaviest || (r.weight || 0) > (t.heaviest.weight || 0)) t.heaviest = r;
  });
  return [...map.values()].map((t) => {
    const h = t.heaviest;
    return {
      id: t.id, records: t.rows.length, weight: t.weight, cbm: t.cbmComplete ? t.cbm : null,
      lane: h.lane, srcCity: h.srcCity, dstCity: h.dstCity, transporter: h.transporter,
      vehicle: h.vehicle, capKg: h.capKg, capCbm: h.capCbm, entity: h.entity, month: h.month,
      veh: h.veh, dispatch: h.dispatch,
    };
  });
}
